import { useQuery, useMutation } from "@apollo/client/react";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import { useCallback, useMemo } from "react";
import { GetTaskssDocument, GetTasksDocument, CreateTasksDocument, UpdateTasksDocument, DeleteTasksDocument, GetTaskssQuery, GetTasksQuery, CreateTasksMutation, UpdateTasksMutation, DeleteTasksMutation, Contact_TasksInsertInput } from "@/src/gql/graphql";
import { TASK_CONFIG } from "@/features/tasks/components/TaskConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

// ============================================================================
// HELPER: assigned_users normalization
// ============================================================================

/**
 * Collapse a task's `assigned_users` down to the plain `app_users` ids the
 * column actually stores.
 *
 * This layer used to do the opposite: it fetched each id's `app_users` row and
 * SWAPPED the id for the row in place, so the value the task hooks handed out
 * carried OBJECTS where `ContactTaskRow` promised strings. That is why the
 * function ended in `return enriched as ContactTaskRow[]`, and why
 * `EntityHooks<T>` looked like it needed a separate read generic (#2634).
 *
 * Nothing consumed the object form. Every assignee surface resolves names from
 * the user directory BY ID and would have done so anyway:
 * `MultiUserDisplay` / `ExpandedMultiUserDisplay` collapse whatever they are
 * given to ids and look each one up in `useAdminUsers()`, and
 * `TaskAssigneesDisplay` additionally queries `app_users` itself for any id the
 * directory does not carry — so `collectEmbeddedAssigneeMeta`'s objects were a
 * strictly-redundant third source behind two better ones.
 *
 * The swap was also breaking a real surface: `TaskInformationSection` reads
 * `(item.assigned_users ?? []).filter(isNonEmptyString)`, so every object
 * element was dropped and an assigned task rendered "Unassigned".
 *
 * Normalizing to ids instead makes `ContactTaskRow` true on the read side with
 * no change to the write side — no cast, no third generic, and one fewer
 * network round trip per task page. Legacy object elements (a row cached before
 * this change, a form control that emits `{ id }`) are still collapsed here
 * rather than trusted.
 */
export function normalizeTaskAssignedUsers(tasks: ContactTaskRow[]): ContactTaskRow[] {
  let changed = false;
  const normalized = tasks.map((task) => {
    const current = task.assigned_users;
    if (!Array.isArray(current) || current.every((item) => typeof item === "string")) {
      return task;
    }
    changed = true;
    return { ...task, assigned_users: normalizeAssignedUserIds(current) ?? [] };
  });
  return changed ? normalized : tasks;
}

type AssignedUserValue = string | { id?: string | null } | null | undefined;

function normalizeAssignedUserIds(
  assignedUsers: AssignedUserValue[] | null | undefined
): string[] | undefined {
  if (!assignedUsers || !Array.isArray(assignedUsers)) {
    return undefined;
  }

  const normalized = assignedUsers
    .map((item) => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item !== null && typeof item.id === "string") {
        return item.id;
      }
      return null;
    })
    .filter((uid): uid is string => uid !== null);

  return normalized.length > 0 ? normalized : undefined;
}

/**
 * Task hooks using generated GraphQL operations.
 *
 * Allocated ONCE at module scope: the object is a dependency of
 * `useBuildActionContext`'s `useMemo` and closes over nothing render-scoped.
 * See the note in `contacts-graphql.ts`.
 */
const TASK_ENTITY_HOOKS: EntityHooks<ContactTaskRow> = {
  queryDocument: GetTaskssDocument,
  usePaginated: (params?: Record<string, unknown>) => {
    const result = useQuery<GetTaskssQuery>(GetTaskssDocument, {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      variables: params,
    });

    const nodes = useMemo(
      () =>
        normalizeTaskAssignedUsers(
          result.data?.contact_tasksCollection?.edges
            ?.map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => node != null) || []
        ),
      [result.data]
    );

    return useMemo(
      () => ({
        data: result.data
          ? {
              data: nodes,
              totalCount: result.data.contact_tasksCollection?.totalCount || 0,
              pageInfo: result.data.contact_tasksCollection?.pageInfo,
            }
          : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
        networkStatus: result.networkStatus,
      }),
      [result, nodes]
    );
  },
  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetTasksQuery>(GetTasksDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || options?.enabled === false,
    });

    const rawTask = result.data?.contact_tasksCollection?.edges?.[0]?.node || null;
    const task = useMemo(
      () => (rawTask ? normalizeTaskAssignedUsers([rawTask])[0] : null),
      [rawTask]
    );

    return useMemo(
      () => ({
        data: task ?? undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      }),
      [result, task]
    );
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateTasksMutation>(UpdateTasksDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & Partial<ContactTaskRow>) => {
        const cleanInput = { ...input } as Partial<ContactTaskRow> & Record<string, unknown>;

        const normalizedAssignedUsers = normalizeAssignedUserIds(cleanInput.assigned_users);
        if (normalizedAssignedUsers) {
          cleanInput.assigned_users = normalizedAssignedUsers;
        }

        const prepared = prepareEntityGraphQlMutationInput(
          { ...cleanInput },
          TASK_CONFIG.fields,
          {
            mode: "update",
          }
        );

        const { data, error } = await mutate({
          variables: { id, input: prepared },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the task.");

        const updated = data?.updatecontact_tasksCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the task.");

        // No hand-patched caches here any more. `usePaginated`/`useSingle` used
        // to prefer a React Query "enrichment" layer keyed by the task ID LIST
        // ONLY, so a field change did not change the key and the stale enriched
        // value kept winning over the fresh Apollo node — which is why both the
        // list cache (`["tasks-with-users", …]`) and the detail cache
        // (`["task-with-users", id]`, which the plural prefix never matched)
        // had to be patched by hand after every write.
        //
        // That layer is gone (see `normalizeTaskAssignedUsers`). Both hooks now
        // read the Apollo node directly, and `UpdateTasks` selects exactly the
        // same 16 columns as `GetTaskss`/`GetTasks`, so InMemoryCache
        // normalizes the returned record onto `contact_tasks:<id>` and every
        // watching query re-renders with the new values on its own.
        return updated;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteTasksMutation>(DeleteTasksDocument);
    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the task.");
        const deletedId = data?.deleteFromcontact_tasksCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the task.");
        return deletedId;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateTasksMutation>(CreateTasksDocument);
    const mutateAsync = useCallback(
      async (input: Contact_TasksInsertInput) => {
        const cleanInput = prepareEntityGraphQlMutationInput({ ...input }, TASK_CONFIG.fields, {
          mode: "insert",
        });
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the task.");
        const created = data?.insertIntocontact_tasksCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the task.");
        return created;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },

  getFieldConfig: (fieldName: string) => {
    return TASK_CONFIG.fields[fieldName] || null;
  },

  getTableConfig: () => {
    return TASK_CONFIG;
  },
};

export const useTaskHooks = (): EntityHooks<ContactTaskRow> => TASK_ENTITY_HOOKS;
